import { Body, RotationalSpring } from "p2";

import { angleDelta } from "../../core/util/MathUtil";

/**
 * A rotational spring that applies corrective torque to maintain a target angle.
 * Useful for stabilizing physics bodies or creating aiming/orientation behaviors
 * with damping to prevent oscillation.
 *
 * ## When to Use:
 * - **Stabilizing Platforms**: Keep rotating platforms level despite physics forces
 * - **Aiming Systems**: Smoothly rotate turrets or weapons toward targets
 * - **Vehicle Handling**: Auto-correct vehicle orientation for better control
 * - **Pendulum Systems**: Create realistic swinging objects with natural damping
 *
 * ## Entity Integration:
 * ```typescript
 * class Turret extends BaseEntity {
 *   aimSpring?: AimSpring;
 *
 *   onAdd() {
 *     // Create physics body first
 *     this.body = new Body({ mass: 1 });
 *     this.game.world.addBody(this.body);
 *
 *     // Add aim spring for smooth rotation
 *     this.aimSpring = new AimSpring(this.body);
 *     this.game.world.addSpring(this.aimSpring);
 *   }
 *
 *   onTick() {
 *     // Update target angle based on player position
 *     const player = this.game.entities.getTagged("player")[0];
 *     if (player) {
 *       const angle = Math.atan2(
 *         player.position.y - this.position.y,
 *         player.position.x - this.position.x
 *       );
 *       this.aimSpring!.restAngle = angle;
 *     }
 *   }
 * }
 * ```
 *
 * ## Configuration:
 * - **stiffness**: How strongly it pulls toward the target angle (default: 10)
 * - **damping**: How much it resists oscillation (default: 1)
 * - **restAngle**: Target angle in radians (updated each tick for aiming)
 */
export default class AimSpring extends RotationalSpring {
  constructor(
    bodyA: Body,
    options?: {
      stiffness?: number;
      damping?: number;
    },
  ) {
    const { stiffness = 10, damping = 1 } = options || {};
    super(bodyA, null as any, {
      damping,
      stiffness,
      restAngle: 0,
    });
  }

  applyForce() {
    const body = this.bodyA;
    const displacement = angleDelta(this.restAngle, body.angle);

    let torque =
      -this.stiffness * displacement - this.damping * body.angularVelocity;

    // Apply a non-linear power curve to the torque
    torque = Math.sign(torque) * Math.abs(torque) ** 0.6 * 100;

    body.angularForce += torque;
  }
}
